import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import './CustomerCrm.css';
import CrmReminder from './CrmReminder';
import CustomerCrmHistory, { CrmHistoryIcon } from './CustomerCrmHistory';

const timestamp = value => new Date(value).toLocaleString('en-US', {
  timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short',
});

export default function CustomerCrm({ customerId, showRefresh = true, focusMessageId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [canWrite, setCanWrite] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [history, setHistory] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    const refreshReminders = () => setRefresh(value => value + 1);
    window.addEventListener('crm-reminder-changed', refreshReminders);
    return () => { clearInterval(timer); window.removeEventListener('crm-reminder-changed', refreshReminders); };
  }, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const conversation = useRef(null);
  const focusedMessage = useRef(null);
  const mounted = useRef(true);
  const endpoint = `/customers/${encodeURIComponent(customerId)}/crm`;

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    api.get(endpoint).then(({ data }) => {
      if (current) { setMessages(data.messages); setCanWrite(data.canWrite); setCanDelete(data.canDelete); }
    }).catch(err => { if (current) setError(err.response?.data?.error || 'Unable to load customer notes.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [endpoint, refresh]);

  useEffect(() => {
    if (!loading && focusMessageId && focusedMessage.current) {
      focusedMessage.current.scrollIntoView({ block: 'center' });
      focusedMessage.current.focus({ preventScroll: true });
    } else if (conversation.current) conversation.current.scrollTop = conversation.current.scrollHeight;
  }, [messages.length, loading, focusMessageId]);

  useEffect(() => {
    if (loading || !focusMessageId || !messages.some(message => String(message.id) === String(focusMessageId))) return;
    api.put(`/crm-inbox/${encodeURIComponent(focusMessageId)}/read`, { version: messages.find(message => String(message.id) === String(focusMessageId)).version })
      .then(() => window.dispatchEvent(new Event('crm-inbox-changed'))).catch(() => {});
  }, [loading, focusMessageId, messages]);

  const save = async event => {
    event.preventDefault();
    if (saving || !(editing ? editBody : draft).trim()) return;
    setSaving(true); setError('');
    try {
      const { data } = editing
        ? await api.put(`${endpoint}/${editing.id}`, { body: editBody, version: editing.version })
        : await api.post(endpoint, { body: draft });
      if (!mounted.current) return;
      setMessages(current => editing ? current.map(message => message.id === data.id ? { ...message, ...data } : message) : [...current, data]);
      if (editing) { setEditing(null); setEditBody(''); } else setDraft('');
      window.dispatchEvent(new Event('crm-inbox-changed'));
    } catch (err) {
      if (mounted.current) setError(err.response?.data?.error || 'Unable to save the message. Please try again.');
    } finally { if (mounted.current) setSaving(false); }
  };

  const deleteMessage = async message => {
    if (saving) return;
    setSaving(true); setError('');
    try {
      await api.delete(`${endpoint}/${message.id}`, { data: { version: message.version } });
      window.dispatchEvent(new Event('crm-inbox-changed'));
      if (!mounted.current) return;
      setMessages(current => current.filter(item => item.id !== message.id));
      if (editing?.id === message.id) { setEditing(null); setEditBody(''); }
      setDeleting(null);
    } catch (err) {
      if (mounted.current) setError(err.response?.data?.error || 'Unable to delete the message. Please try again.');
    } finally { if (mounted.current) setSaving(false); }
  };

  const handleDraftKeyDown = event => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    if (event.altKey) {
      const input = event.currentTarget;
      const start = input.selectionStart;
      const nextDraft = draft.slice(0, start) + '\n' + draft.slice(input.selectionEnd);
      if (nextDraft.length > 10000) return;
      setDraft(nextDraft);
      requestAnimationFrame(() => input.setSelectionRange(start + 1, start + 1));
      return;
    }
    if (!editing && !event.repeat) save(event);
  };

  return <section className="customer-crm" aria-label="Customer CRM conversation">
    <div className="crm-heading"><div><h3>CRM</h3><p>Internal customer notes · All times Eastern</p></div>
      <div className="crm-actions">
        <button type="button" className="crm-refresh-icon" title="CRM history" aria-label="View all CRM history" onClick={() => setHistory({ messageId: null })}><CrmHistoryIcon /></button>
        {showRefresh && <button type="button" className={`crm-refresh-icon${loading ? ' refreshing' : ''}`} title="Refresh CRM" aria-label="Refresh CRM" disabled={loading || saving} onClick={() => { setEditing(null); setRefresh(value => value + 1); }}><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 5v4h4m-4 4a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4" /></svg></button>}
      </div></div>
    {error && <p className="crm-error" role="alert">{error}</p>}
    {!loading && !error && focusMessageId && !messages.some(message => String(message.id) === String(focusMessageId)) && <p role="status">This message is no longer available.</p>}
    <div className="crm-conversation" ref={conversation} aria-label="Customer messages" aria-busy={loading}>
      {loading ? <p className="crm-empty">Loading notes...</p> : !messages.length ? <p className="crm-empty">No notes yet. Start the conversation below.</p> : messages.map(message =>
        <article key={message.id} ref={String(message.id) === String(focusMessageId) ? focusedMessage : null} tabIndex={String(message.id) === String(focusMessageId) ? -1 : undefined} data-inbox-target={String(message.id) === String(focusMessageId)} className={`crm-message ${String(message.authorId) === String(user?.id) ? 'crm-message-own' : ''}`}>
          <div className="crm-message-header">
            <div className="crm-meta"><strong>{message.authorName}</strong><time dateTime={message.createdAt}>{timestamp(message.createdAt)}</time></div>
            <div className="crm-message-icons">

            {canWrite && editing?.id !== message.id && <button type="button" className="crm-edit-icon" disabled={saving} title="Edit message" aria-label={`Edit message by ${message.authorName}`} onClick={() => { setEditing(message); setEditBody(message.body); }}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m16 3 5 5M4 15 16 3a2.1 2.1 0 0 1 3 0l2 2a2.1 2.1 0 0 1 0 3L9 20l-6 1z" /></svg>
            </button>}
<button type="button" className={`crm-edit-icon${new Date(message.reminderDueAt).getTime() > now ? ' crm-reminder-scheduled' : ''}`} title={new Date(message.reminderDueAt).getTime() > now ? `Reminder scheduled: ${timestamp(message.reminderDueAt)}` : 'Schedule reminder'} aria-label={`Schedule reminder for message by ${message.authorName}`} onClick={() => setReminder(message)}><svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l3 2M3 5l3-3m12 0 3 3M6 20l-1 2m13-2 1 2" /></svg></button>
            <button type="button" className="crm-edit-icon" title="Message history" aria-label={`View history of message by ${message.authorName}`} onClick={() => setHistory({ messageId: message.id })}><CrmHistoryIcon /></button>
            {canDelete && <button type="button" className="crm-edit-icon crm-delete-icon" disabled={saving} title="Delete message" aria-label={`Delete message by ${message.authorName}`} onClick={() => setDeleting(message.id)}>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7" /></svg>
            </button>}
            </div>
          </div>
          {deleting === message.id && <div className="crm-delete-confirm" role="group" aria-label="Confirm message deletion">
            <span>Delete this message?</span>
            <button type="button" disabled={saving} onClick={() => setDeleting(null)}>Cancel</button>
            <button type="button" disabled={saving} onClick={() => deleteMessage(message)}>{saving ? 'Deleting...' : 'Delete'}</button>
          </div>}
          {editing?.id === message.id ? <form onSubmit={save}>
            <textarea aria-label="Edit message" value={editBody} onChange={event => setEditBody(event.target.value)} maxLength={10000} rows={4} disabled={saving} autoFocus />
            <div className="crm-actions"><button type="button" disabled={saving} onClick={() => setEditing(null)}>Cancel</button><button type="submit" disabled={saving || !editBody.trim()}>{saving ? 'Saving...' : 'Save changes'}</button></div>
          </form> : <><p className="crm-body">{message.body}</p><div className="crm-message-footer">
            {message.editedAt && <small>Edited by {message.editedBy} · {timestamp(message.editedAt)}</small>}

          </div></>}
        </article>)}
    </div>
    {canWrite && !loading ? <form className="crm-composer" onSubmit={save}>
      <label htmlFor={`crm-draft-${customerId}`}>Add a note</label>
      <textarea id={`crm-draft-${customerId}`} placeholder="Write a customer note..." value={draft} maxLength={10000} rows={3} disabled={saving} onChange={event => setDraft(event.target.value)} onKeyDown={handleDraftKeyDown} />
      <small>Enter to post · Alt+Enter for a new line</small>
      <div className="crm-actions"><small>{draft.length.toLocaleString()} / 10,000</small><button type="submit" disabled={saving || !!editing || !draft.trim()}>{saving && !editing ? 'Posting...' : 'Post message'}</button></div>
    </form> : !loading && !error && <p className="crm-empty">You have read-only access to these notes.</p>}
    {reminder && <CrmReminder message={reminder} onClose={() => setReminder(null)} onSaved={reminderDueAt => setMessages(current => current.map(message => message.id === reminder.id ? { ...message, reminderDueAt } : message))} />}
    {history && <CustomerCrmHistory endpoint={endpoint} messageId={history.messageId} onClose={() => setHistory(null)} />}
  </section>;
}
